/** 
 Copyright Highway9 Networks Inc. 
 */
import React, { useEffect, useState, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { history, updateURL } from '~/helpers/history';
import { DISCONNECTED, DEVICE_VIEW } from "../../constants";
import { Types } from "../../constants/types";
import { useInterval } from "../../hooks/useInterval";
import {
  deviceGroupActions,
  deviceGroupSelectedIDs,
  fetchDeviceGroups,
  subscriberDeviceGroups,
} from "../../store/context/deviceGroupSlice";
import {
  fetchDNN,
  fetchDevices,
  fetchDevicesLatestMetrics,
  subscriberActions,
  subscriberData,
  fetchSubscriberDeviceInfo,
  subscriberPreset,
  subscriberSelected,
  subscriberMetricsClient1,
  subscriberMetricsClient2,
  timelineClient,
} from "../../store/context/subscriberSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Device } from "../../types/device";
import { DeviceGroup } from "../../types/deviceGroup";
import moment from "moment-timezone";
import { setSelectedID, utilityActions } from "~/store/context/utilitySlice";
import { SelectChangeEvent } from "@h9/ui-lib";
import { DEVICES, SUBSCRIBER_TAB_NAMES } from "~/constants/routes";

export const defaultViewHiddenColumns = [
  "deviceGroupNameForTopN",
  "runtimeInfo.apn",
  "runtimeInfo.ipv4",
  "runtimeInfo.macAddress",
  "ueId",
  "uplinkSinr",
  "pDownlinkCqi",
  "pDownlinkMcs",
  "uplinkMcs",
  "p1DownlinkBler",
  "p2DownlinkBler",
  "uplinkBler",
  "downlinkRate",
  "uplinkRate",
  "pathloss",
  "txPower",
  "signalStrength",
  "PHY-DL",
  "RI-Rx-Tx",
  "PHY-UL",
  "rssi"
];
export const topNViewHiddenColumns = [
  "deviceGroupName",
  "lte.state",
  "status",
  "statusMessage",
  "runtimeInfo.apn",
  "iccid",
  "simState",
  "runtimeInfo.macAddress"
];

export const AIRSPANColumns = [
  "PHY-DL",
  "RI-Rx-Tx",
  "PHY-UL",
  "rssi"
]
export const AIRSPANOnlyColumns = [
  "RI-Rx-Tx",
  "rssi"
]
export const HiddenColumnsForAirspanAndCLASS_M = [
  "downlinkRate",
  "uplinkRate",
  "pathloss",
  "txPower",
]

export const useDevicePage = () => {
  const dispatch = useAppDispatch();
  const subscribers = useAppSelector(subscriberData);
  const deviceGroups = useAppSelector(subscriberDeviceGroups);
  const selectedDevice = useAppSelector(subscriberSelected);
  const selectedGroupIDS = useAppSelector(deviceGroupSelectedIDs);
  const loadingStatus = useAppSelector((state) => state.subscriber.status);

  const [tabIndex, setTabIndex] = React.useState<string>(SUBSCRIBER_TAB_NAMES.SUMMARY);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [simTypeFilter, setSimTypeFilter] = useState('ALL');
  const showSyntheticSims = useAppSelector((state) => state.deviceGroup.showSyntheticSims);

  const preset = useAppSelector(subscriberPreset);

  const [isLoading, setIsLoading] = useState(true);

  const handleSimTypeChange = (event: SelectChangeEvent<string | number | (string | number)[]>) => {
    setSimTypeFilter(String(event.target.value));
  };

  // update the device list when the device group is changed
  const devices = useMemo(() => {
    const filteredDevices =
      preset === DEVICE_VIEW.TOP_N_VIEW
        ? subscribers.filter((item) => {
          const currentState = item?.runtimeInfo?.status ?? DISCONNECTED;
          return (
            ![DISCONNECTED].includes(currentState) &&
            (selectedGroupIDS.includes(item.groupId!) || selectedGroupIDS.includes(Types.all))
          );
        })
        : subscribers.filter((item) => {
          return selectedGroupIDS.includes(item.groupId!) || selectedGroupIDS.includes(Types.all);
        });

    // Apply simType filter
    const devices =
      simTypeFilter === 'ALL'
        ? filteredDevices
        : filteredDevices.filter((device) => device.simType === simTypeFilter);

    // Hide synthetic SIMs unless explicitly allowed
    const syntheticFiltered = showSyntheticSims
      ? devices
      : devices.filter((device) => !device.simType.includes("SYNTHETIC"));

    if (syntheticFiltered?.length > 0) setIsLoading(false);
    return syntheticFiltered;
  }, [subscribers, preset, selectedGroupIDS, simTypeFilter, showSyntheticSims]);

  // for loading state update
  useEffect(() => {
    if (loadingStatus === "devices_loaded") {
      setIsLoading(false);
    }
  }, [loadingStatus]);

  const getObjectFromIDCallback = (id: string | null) => {
    // Check if devices and subscribers are loaded , wait for them to load

    console.log("devices", devices);
    console.log("subscribers", subscribers);
    if (!id) { 
      dispatch(setSelectedID(devices[0]?.id!));
      return {
      type: Types.device,
      obj: devices[0],
      index : devices.findIndex((item) => item.id === subscribers[0].id)
    }
  }
    dispatch(setSelectedID(id));
    const type = id.split("-")[0];
    switch (type) {
      case Types.device:
      case Types.endUserDevice:
        return {
          type: Types.device,
          obj: subscribers.find((item) => item.id === id),
          index : devices.findIndex((item) => item.id === id)
        };
      case Types.deviceGroup:
        return {
          type: Types.deviceGroup,
          obj: deviceGroups.find((item) => item.id === id),
          index : deviceGroups.findIndex((item) => item.id === id)
        };
    }
  };

  const getObjectFromIDRef = useRef(getObjectFromIDCallback);

  useLayoutEffect(() => {
    getObjectFromIDRef.current = getObjectFromIDCallback;
  });

  const getObjectFromID = useCallback((id: string | null) => {
    return getObjectFromIDRef.current(id);
  }, []);

  // get the params from the url and set the selected object in the store
  useEffect(() => {
    function handleSelectionByURL() {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("id");

      const selected = getObjectFromID(id);
      console.log("selected", selected);
      if (selected?.obj) {
        switch (selected.type) {
          case Types.device:
            dispatch(subscriberActions.setValues(selected.obj as Device));
            setSelectedIndex(selected.index);
            break;
          case Types.deviceGroup:
            dispatch(deviceGroupActions.setValues(selected.obj as DeviceGroup));
            break;
        }
      }
      const menuID = params.get("menuID");
      if (menuID) {
        dispatch(deviceGroupActions.setSelectedDeviceGroupIDs([menuID] as string[]));
      }

      const tab = params.get("tab");
      if (tab) {
        setTabIndex(tab);
      }

      // set preset based on the url
      const view = params.get("preset");
      if (view) {
        dispatch(subscriberActions.setPreset(view));
        dispatch(
          subscriberActions.setHiddenColumns(view === DEVICE_VIEW.TOP_N_VIEW ? topNViewHiddenColumns : defaultViewHiddenColumns)
        );
      }
    }

    if (!isLoading) {
      handleSelectionByURL();
    }

    const unListen = history.listen(() => {
      // This callback is called every time the route changes. But it is also called one last time when we move out of the current route, 
      // (before the `unListen()` is called). This is causing issues like bad redux state, query params being overflow to the new page, etc.
      // To prevent this, we will add a check to ensure that the actual handler is called only on the devices page
      if (location.pathname === DEVICES) {
        handleSelectionByURL();
      }
    });

    return () => { unListen(); };

  }, [dispatch, isLoading, getObjectFromID]);

  // fetch on load
  useEffect(() => {
    (async () => {
      await dispatch(fetchDeviceGroups());
      await dispatch(fetchDevices());
      dispatch(fetchDevicesLatestMetrics());
      dispatch(fetchSubscriberDeviceInfo());
    })();

    return () => {
      // clear the metrics clients
      subscriberMetricsClient1.terminate();
      subscriberMetricsClient2.terminate();
      timelineClient.terminate();
    }
  }, [dispatch]);

  // fetch every 30 seconds for latest metrics.
  useInterval(async () => { dispatch(fetchDevicesLatestMetrics()); }, 1000 * 30);

  function cleanAll() {
    setIsLoading(true);
  }

  useEffect(() => {
    dispatch(fetchDNN());
    return () => {
      cleanAll();
    };
  }, [dispatch]);

  useIntervalSetter("3hour", tabIndex);


  function handlePresetChange(event: SelectChangeEvent<string | number | (string | number)[]>) {
    const value = String(event.target.value);
    dispatch(subscriberActions.setPreset(value));
    if (value === DEVICE_VIEW.TOP_N_VIEW) {
      dispatch(subscriberActions.setHiddenColumns(topNViewHiddenColumns));
    } else {
      dispatch(subscriberActions.setHiddenColumns(defaultViewHiddenColumns));
    }
    updateURL({
      preset: value,
    });
  }

  return {
    isLoading,
    setIsLoading,
    data: subscribers,
    deviceGroups,
    preset,
    devices,
    selectedDevice,
    tabIndex,
    setTabIndex,
    selectedIndex,
    setSelectedIndex,
    handlePresetChange,
    handleSimTypeChange,
    simTypeFilter
  };
};



/**
 * The function `useIntervalSetter` in TypeScript React sets a default time range to 6 hours if the
 * time difference between start and end times is greater than 2 days to improve user experience with
 * large data loading.
 * @param interval - The `interval` parameter in the `useIntervalSetter` function is a string that
 * specifies the time interval for setting the default time range. In this case, it is set to "6hour".
 * @param [difference=2] - The `difference` parameter in the `useIntervalSetter` function represents
 * the threshold value in days. If the time difference between the `startTimeI` and `endTimeI` exceeds
 * this threshold, the default time range will be set to 6 hours to improve user experience due to
 * potentially large data that
 */
export function useIntervalSetter(interval: "3hour", tab: string, difference = 2) {

  const dispatch = useAppDispatch();
  const startTimeI = useAppSelector((state) => state.utility.time.startTime);
  const endTimeI = useAppSelector((state) => state.utility.time.endTime);

  // if time difference is greater than 2 days, then we should set the default time range to 6 hours as due to large data, it will take time to load the data and it will not be a good user experience
  useEffect(() => {
    console.log(">>> in useEffect of Time Setter", moment.unix(endTimeI).diff(moment.unix(startTimeI), "days"));
    if (moment.unix(endTimeI).diff(moment.unix(startTimeI), "days") > difference) {
      dispatch(utilityActions.setTimeInterval({ interval, shouldUpdateTime: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difference, dispatch, interval, tab]);
}